import React, { useState, useEffect, useMemo } from 'react';
import { X, Plus, Minus, Upload, AlertCircle, Check, Package, FileText, Loader2 } from 'lucide-react';
import { RMAMotivo } from '../../types';
import { useRMACart, RMACartUnit, RMA_NOTE_MAX } from '../../context/RMACartContext';
import { uploadRMAAttachment } from '../../services/rmaService';
import { toast } from 'sonner';
import { numeroDocumento } from '../../utils/numeroDocumento';
import { AllegatoRma } from './AllegatoRma';

interface AddToRMAModalProps {
  open: boolean;
  onClose: () => void;
  product: {
    sku: string;
    title: string;
    brand: string;
    category: string;
    dtkCodice?: string;
    codiceOem?: string;
    orderId: string;
    orderDate: string;
    orderStatus: string;
    docTipork?: string;
    docAnno?: number;
    docSerie?: string;
    docNumdoc?: number;
    maxQty: number;
    qtyPurchased: number;
    unitPrice: number;
  } | null;
}

const MOTIVI: Array<{ value: RMAMotivo; label: string; needsPhoto: boolean }> = [
  { value: 'difetto_stampa',                   label: 'Difetto di stampa / resa insufficiente', needsPhoto: true },
  { value: 'prodotto_danneggiato',             label: 'Prodotto danneggiato',                   needsPhoto: true },
  { value: 'articolo_errato_ricevuto',         label: 'Articolo errato ricevuto',               needsPhoto: true },
  { value: 'articolo_ordinato_erroneamente',   label: 'Articolo ordinato erroneamente',         needsPhoto: false },
];

/** L'errato acquisto ("articolo ordinato erroneamente") è ammesso solo entro
 * 30 giorni dalla data di acquisto (richiesta utente giugno 2026). Regola
 * applicata anche lato backend alla creazione della pratica. */
const ERRATO_ACQUISTO_MOTIVO: RMAMotivo = 'articolo_ordinato_erroneamente';
const ERRATO_ACQUISTO_MAX_DAYS = 30;

function daysSince(dateStr?: string): number | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024; // 5 MB
const ACCEPTED_MIMES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
const ACCEPT_ATTR = 'image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf';

const uid = () => `u_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

export function AddToRMAModal({ open, onClose, product }: AddToRMAModalProps) {
  const { addItem, items: cartItems } = useRMACart();
  const [qty, setQty] = useState(1);
  const [units, setUnits] = useState<RMACartUnit[]>([]);
  const [uploadingIds, setUploadingIds] = useState<Set<string>>(new Set());

  // Quanti pezzi di questo SKU sono già nel carrello reso (stesso ordine)
  const alreadyInCart = useMemo(() => {
    if (!product) return 0;
    const existing = cartItems.find(i => i.orderId === product.orderId && i.sku === product.sku);
    return existing?.units.length || 0;
  }, [cartItems, product]);

  const remainingQty = product ? product.maxQty - alreadyInCart : 0;
  const purchaseAgeDays = daysSince(product?.orderDate);
  const erratoAcquistoScaduto = purchaseAgeDays !== null && purchaseAgeDays > ERRATO_ACQUISTO_MAX_DAYS;

  useEffect(() => {
    if (open && product) {
      const initialQty = remainingQty > 0 ? 1 : 0;
      setQty(initialQty);
      setUnits(initialQty > 0 ? [{ id: uid(), motivo: '', descrizione: '' }] : []);
    }
  }, [open, product, remainingQty]);

  if (!open || !product) return null;

  const adjustQty = (delta: number) => {
    const newQty = Math.max(1, Math.min(remainingQty, qty + delta));
    setQty(newQty);
    setUnits(prev => {
      if (newQty > prev.length) {
        const toAdd = Array.from({ length: newQty - prev.length }, () => ({ id: uid(), motivo: '' as RMAMotivo | '', descrizione: '' }));
        return [...prev, ...toAdd];
      }
      return prev.slice(0, newQty);
    });
  };

  const updateUnit = (id: string, patch: Partial<RMACartUnit>) => {
    setUnits(prev => prev.map(u => u.id === id ? { ...u, ...patch } : u));
  };

  const handlePhoto = async (id: string, file: File) => {
    // Validazione formato
    const mime = file.type || '';
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const isAccepted = ACCEPTED_MIMES.includes(mime) || ['jpg', 'jpeg', 'png', 'pdf'].includes(ext);
    if (!isAccepted) {
      toast.error('Formato non supportato', { description: 'Sono ammessi solo JPG, PNG o PDF.' });
      return;
    }
    // Validazione dimensione
    if (file.size > MAX_ATTACHMENT_BYTES) {
      toast.error('File troppo grande', { description: 'La dimensione massima consentita è 5 MB.' });
      return;
    }
    setUploadingIds(prev => new Set(prev).add(id));
    try {
      const result = await uploadRMAAttachment(file);
      updateUnit(id, { photoPath: result.path, photoUrl: result.url, photoName: result.name || file.name, photoMime: result.mime });
    } catch (err: any) {
      toast.error('Caricamento allegato non riuscito', {
        description: err?.response?.data?.detail || 'Riprova più tardi.',
      });
    } finally {
      setUploadingIds(prev => { const next = new Set(prev); next.delete(id); return next; });
    }
  };

  const removePhoto = (id: string) => updateUnit(id, { photoPath: undefined, photoUrl: undefined, photoName: undefined, photoMime: undefined });

  const isUnitValid = (u: RMACartUnit): boolean => {
    if (!u.motivo) return false;
    if (u.motivo === ERRATO_ACQUISTO_MOTIVO && erratoAcquistoScaduto) return false;
    const m = MOTIVI.find(mt => mt.value === u.motivo);
    if (m?.needsPhoto && !u.photoUrl) return false;
    return true;
  };

  const isUploading = uploadingIds.size > 0;
  const allValid = units.length > 0 && units.every(isUnitValid) && !isUploading;

  const handleSubmit = () => {
    if (!allValid || !product) return;
    addItem({
      sku: product.sku,
      title: product.title,
      brand: product.brand,
      category: product.category,
      dtkCodice: product.dtkCodice,
      codiceOem: product.codiceOem,
      orderId: product.orderId,
      orderDate: product.orderDate,
      orderStatus: product.orderStatus,
      docTipork: product.docTipork,
      docAnno: product.docAnno,
      docSerie: product.docSerie,
      docNumdoc: product.docNumdoc,
      maxQty: product.maxQty,
      qtyPurchased: product.qtyPurchased,
      unitPrice: product.unitPrice,
      units,
    });
    toast.success(`${units.length} ${units.length === 1 ? 'pezzo aggiunto' : 'pezzi aggiunti'} al carrello resi`, {
      description: `${product.title} - Documento ${numeroDocumento(product.orderId)}`,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" data-testid="add-rma-modal">
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="border-b px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-[hsl(214,58%,41%)]/10 flex items-center justify-center flex-shrink-0">
              <Package className="w-4 h-4 text-[hsl(214,58%,41%)]" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">Aggiungi al carrello resi</div>
              <div className="text-xs text-muted-foreground truncate">Documento {numeroDocumento(product.orderId)} · {new Date(product.orderDate).toLocaleDateString('it-IT')}</div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-secondary flex items-center justify-center flex-shrink-0" data-testid="rma-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* Product info */}
          <div className="rounded-xl border bg-[hsl(var(--dtk-canvas))] p-4">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              {product.dtkCodice && <span className="font-mono text-[10px] font-bold text-[hsl(214,58%,41%)]">DTK: {product.dtkCodice}</span>}
              <span className="font-mono text-[10px] text-muted-foreground">Cod. {product.sku}</span>
              <span className="text-[10px] font-bold border px-1.5 py-0.5 rounded bg-white">{product.brand}</span>
            </div>
            <div className="text-sm font-medium">{product.title}</div>
            <div className="text-xs text-muted-foreground mt-1">€ {product.unitPrice.toFixed(2).replace('.', ',')} cad. · Acquistati: {product.qtyPurchased} pz · Disponibili al reso: {remainingQty}/{product.maxQty}</div>
          </div>

          {remainingQty <= 0 ? (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-900">
                Hai già aggiunto al carrello resi tutti i pezzi disponibili di questo prodotto da questo ordine.
              </div>
            </div>
          ) : (
            <>
              {/* Quantity selector */}
              <div className="rounded-xl border bg-white p-4">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2.5 block">Quantità da rendere</label>
                <div className="flex items-center gap-3">
                  <div className="flex items-center border rounded-lg overflow-hidden h-10">
                    <button
                      onClick={() => adjustQty(-1)}
                      disabled={qty <= 1}
                      className="w-10 h-10 flex items-center justify-center hover:bg-secondary disabled:opacity-40"
                      data-testid="rma-modal-qty-minus"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="w-12 text-center text-sm font-semibold border-x h-10 flex items-center justify-center" data-testid="rma-modal-qty">{qty}</span>
                    <button
                      onClick={() => adjustQty(1)}
                      disabled={qty >= remainingQty}
                      className="w-10 h-10 flex items-center justify-center hover:bg-secondary disabled:opacity-40"
                      data-testid="rma-modal-qty-plus"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <span className="text-xs text-muted-foreground">Max {remainingQty} pz</span>
                </div>
              </div>

              {/* Per-unit details */}
              <div className="space-y-3">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">Dettagli per pezzo</label>
                {units.map((u, idx) => {
                  const motivo = MOTIVI.find(m => m.value === u.motivo);
                  const photoRequired = motivo?.needsPhoto && !u.photoUrl;
                  return (
                    <div key={u.id} className="rounded-xl border bg-white p-4 space-y-3" data-testid={`rma-modal-unit-${idx}`}>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-[hsl(214,58%,41%)] text-white text-[11px] font-bold flex items-center justify-center">{idx + 1}</div>
                        <span className="text-xs font-semibold">Pezzo {idx + 1} di {units.length}</span>
                        {isUnitValid(u) && <Check className="w-3.5 h-3.5 text-emerald-600 ml-auto" />}
                      </div>

                      {/* Motivo */}
                      <div>
                        <label className="text-[11px] text-muted-foreground block mb-1.5">Motivo del reso *</label>
                        <select
                          value={u.motivo}
                          onChange={e => updateUnit(u.id, { motivo: e.target.value as RMAMotivo })}
                          className="w-full h-10 px-3 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]"
                          data-testid={`rma-modal-motivo-${idx}`}
                        >
                          <option value="">Seleziona un motivo...</option>
                          {MOTIVI.map(m => {
                            const disabled = m.value === ERRATO_ACQUISTO_MOTIVO && erratoAcquistoScaduto;
                            return (
                              <option key={m.value} value={m.value} disabled={disabled}>
                                {m.label}{disabled ? ' — non disponibile oltre 30 giorni' : ''}
                              </option>
                            );
                          })}
                        </select>
                        {erratoAcquistoScaduto && (
                          <p className="text-[11px] text-amber-700 mt-1.5 flex items-start gap-1" data-testid={`rma-modal-errato-acquisto-blocked-${idx}`}>
                            <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            "Articolo ordinato erroneamente" è consentito solo entro 30 giorni dall'acquisto.
                          </p>
                        )}
                      </div>

                      {/* Foto / Allegato */}
                      {motivo?.needsPhoto && (
                        <div>
                          <label className="text-[11px] text-muted-foreground block mb-1.5">
                            Foto o allegato * {photoRequired && <span className="text-red-500">obbligatorio</span>}
                          </label>
                          {uploadingIds.has(u.id) ? (
                            <div className="w-20 h-20 rounded-lg border flex flex-col items-center justify-center bg-secondary/40" data-testid={`rma-modal-photo-uploading-${idx}`}>
                              <Loader2 className="w-5 h-5 animate-spin text-[hsl(214,58%,41%)]" />
                              <span className="text-[9px] text-muted-foreground mt-1">Caricamento...</span>
                            </div>
                          ) : u.photoUrl ? (
                            <div className="relative inline-block">
                              <AllegatoRma
                                url={u.photoUrl}
                                mime={u.photoMime}
                                name={u.photoName}
                                alt="Foto caricata per il reso"
                                imgClassName="w-20 h-20 object-cover rounded-lg border"
                                pdfClassName="w-20 h-20 rounded-lg border bg-red-50 flex flex-col items-center justify-center hover:bg-red-100 transition-colors"
                                pdfContent={<><FileText className="w-7 h-7 text-red-600" /><span className="text-[9px] font-bold text-red-600 mt-0.5">PDF</span></>}
                              />
                              <button
                                onClick={() => removePhoto(u.id)}
                                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
                              >
                                <X className="w-2.5 h-2.5" />
                              </button>
                              <div className="text-[10px] text-muted-foreground mt-1 truncate max-w-[100px]">{u.photoName}</div>
                            </div>
                          ) : (
                            <label className={`inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border-2 border-dashed cursor-pointer text-xs hover:bg-secondary/50 transition-colors ${photoRequired ? 'border-red-300 bg-red-50/30' : 'border-border'}`}>
                              <Upload className="w-3.5 h-3.5" />
                              Carica foto o PDF
                              <input
                                type="file"
                                accept={ACCEPT_ATTR}
                                className="hidden"
                                onChange={e => e.target.files?.[0] && handlePhoto(u.id, e.target.files[0])}
                                data-testid={`rma-modal-photo-${idx}`}
                              />
                            </label>
                          )}
                          <div className="text-[10px] text-muted-foreground mt-1">Formati accettati: JPG, PNG, PDF — max 5 MB</div>
                        </div>
                      )}

                      {/* Note del Cliente sul pezzo (INT-128): facoltative, si
                          modificano nel carrello resi fino alla conferma. */}
                      <div>
                        <label className="text-[11px] text-muted-foreground block mb-1.5">Note</label>
                        <textarea
                          value={u.descrizione}
                          onChange={e => updateUnit(u.id, { descrizione: e.target.value })}
                          maxLength={RMA_NOTE_MAX}
                          rows={2}
                          placeholder="Facoltative: dettagli sul difetto o sul reso"
                          className="w-full px-3 py-2 rounded-lg border text-sm bg-white resize-y focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]"
                          data-testid={`rma-modal-note-${idx}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-5 py-3.5 flex items-center justify-between gap-3 flex-shrink-0 bg-[hsl(var(--dtk-canvas))]">
          <button
            onClick={onClose}
            className="h-10 px-4 rounded-lg border bg-white text-sm font-medium hover:bg-secondary transition-colors"
            data-testid="rma-modal-cancel"
          >
            Annulla
          </button>
          <button
            onClick={handleSubmit}
            disabled={!allValid || remainingQty <= 0}
            className="h-10 px-5 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium flex items-center gap-1.5 hover:bg-[hsl(214,58%,46%)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="rma-modal-add"
          >
            {isUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            {isUploading ? 'Caricamento allegati...' : 'Aggiungi al carrello resi'}
          </button>
        </div>
      </div>
    </div>
  );
}
